import React from 'react';
import { Root, Thumb } from '@radix-ui/react-switch';
import clsx from 'clsx';
import { DataTestId } from '@utils/test';

type ToggleProps = {
  checked: boolean;
  onCheckedChange: () => void;
  leftLabel?: React.ReactNode;
  rightLabel?: React.ReactNode;
  testId?: string;
  disabled?: boolean;
  toggleLabelProps?: Omit<React.ComponentProps<typeof ToggleLabel>, 'children'>;
};

export const Toggle = ({
  checked,
  onCheckedChange,
  leftLabel,
  rightLabel,
  testId = DataTestId.TOGGLE_SWITCH,
  disabled,
  toggleLabelProps,
}: ToggleProps) => {
  return (
    <div className="flex justify-between">
      {!!leftLabel && (
        <ToggleLabel checked={!checked} {...toggleLabelProps}>
          {leftLabel}
        </ToggleLabel>
      )}
      <Root
        className={clsx(
          'relative h-[22px] w-[46px] rounded-full transition-colors duration-150',
          disabled
            ? 'cursor-not-allowed bg-base300_alpha50'
            : checked
            ? 'bg-accent400 focus:shadow-[0px_0px_0px_2px_#EB5CBB] hover:bg-fuchsia500_alpha30'
            : 'bg-base100 focus:shadow-[0px_0px_0px_2px_#1F1F1F] hover:bg-base300_alpha50',
        )}
        data-testid={testId}
        onCheckedChange={disabled ? () => null : onCheckedChange}
        checked={checked}
        disabled={disabled}
      >
        <Thumb
          className={clsx(
            'block h-[16px] w-[16px] rounded-full transition-transform duration-150 will-change-transform',
            checked ? 'translate-x-[28px]' : 'translate-x-[2px]',
            disabled && checked && 'bg-fuchsia500_alpha50',
            disabled && !checked && 'bg-base800_alpha50',
            !disabled && checked && 'bg-fuchsia500',
            !disabled && !checked && 'bg-base800',
          )}
        />
      </Root>
      {!!rightLabel && (
        <ToggleLabel checked={checked} {...toggleLabelProps}>
          {rightLabel}
        </ToggleLabel>
      )}
    </div>
  );
};

export const ToggleLabel = ({
  children,
  checked,
  noMargin,
}: {
  children: React.ReactNode;
  checked?: boolean;
  noMargin?: boolean;
}) => {
  return (
    <span
      className={clsx(
        'text-base-l font-medium',
        checked ? 'text-neutral600' : 'text-neutral400',
        noMargin ? 'mx-[unset]' : 'mx-[8px]',
      )}
    >
      {children}
    </span>
  );
};
